import { FC, useState } from 'react'
import { Box, Typography } from '@mui/material'
import { colorTokens } from '../../../shared/styles/colorTokens'
import { RADIUS } from '../../../shared/styles/radii'
import { typeScale } from '../../../shared/styles/typography'

type FigureProps = {
  src?: string
  alt?: string
}

/**
 * 手順の画面写真。markdown の ![キャプション](/guide/xxx.png) から生成される。
 *
 * 素材は 178x113 のトレイ切り抜きから 1400x794 の全画面まで実寸がばらばらで、
 * そのまま並べると大きさが不揃いに見える。かといって小さい切り抜きを引き伸ばすと
 * 潰れて読めないし、「トレイのアイコン」が全画面ショットと同じ大きさで出るのも嘘になる。
 *
 * そこで画像自体の寸法は素材のままにし、**外側の帯の幅を揃える**ことで縦のリズムを作る。
 * 帯は bg.subtle。28 spec は罫線を最後の手段とし、領域分割は bg.subtle/muted で行う
 * (colorTokens.ts の運用規則)。白地のスクショが本文の白地に溶ける問題も、
 * 罫線ではなく地色で解く。背の高い素材は maxHeight で頭を揃え、
 * 原寸はクリックで**その場で**見せる (もう一度押すと戻る)。
 *
 * モーダルにしないのは、覆うと手順の文が隠れ、閉じる操作を別に用意することになるため。
 * 8/29 に顧客が詳細画面の拡大で不可とした 2 点 (「拡大すると右が消える」「戻る為に
 * 画面の隙間をクリックする必要があり大変」) が、読み物のこの面にもそのまま当たる。
 * design-book 03-patterns/modals.md §1 (まずインライン化) / 04-apps/mamoru.md「サポート資材」。
 */
const FIGURE_MAX_HEIGHT = 400

export const Figure: FC<FigureProps> = ({ src, alt }) => {
  const [isExpanded, setIsExpanded] = useState(false)

  if (!src) return null

  return (
    <Box component="figure" sx={{ m: 0, my: 2 }}>
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          backgroundColor: colorTokens.bg.subtle,
          borderRadius: `${RADIUS.md}px`,
          p: 2,
        }}
      >
        <Box
          component="img"
          src={src}
          alt={alt ?? ''}
          loading="lazy"
          onClick={() => setIsExpanded((v) => !v)}
          sx={{
            display: 'block',
            maxWidth: '100%',
            maxHeight: isExpanded ? 'none' : FIGURE_MAX_HEIGHT,
            width: 'auto',
            height: 'auto',
            borderRadius: `${RADIUS.sm}px`,
            cursor: isExpanded ? 'zoom-out' : 'zoom-in',
          }}
        />
      </Box>
      {alt && (
        <Typography
          component="figcaption"
          sx={{
            ...typeScale.bodySm,
            color: colorTokens.fg.muted,
            mt: 1,
          }}
        >
          {alt}
        </Typography>
      )}
    </Box>
  )
}
